// src/components/pcr/sections/SecondaryAssessment.tsx
import React, { useRef, useEffect } from 'react';
import { useForm } from '../../../contexts/FormContext';
import SectionHeading from '../common/SectionHeading';
import TextareaField from '../common/TextareaField';
import CheckboxGroup from '../common/CheckboxGroup';
import { toggleValue } from '../../../utils/formUtils';

interface SecondaryAssessmentProps {
  sectionHighlight?: string;
}

const SecondaryAssessment: React.FC<SecondaryAssessmentProps> = ({ sectionHighlight }) => {
  const { formData, updateFormField } = useForm();
  const { 
    differentialDiagnosis, 
    physicalExam, 
    hpi, 
    pmh, 
    vitalSigns 
  } = formData;

  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!sectionHighlight || !sectionRef.current) return;
    
    // Add highlight class
    sectionRef.current.classList.add('section-highlight');
    
    // Scroll the section into view
    sectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    
    // Remove highlight class after animation
    setTimeout(() => {
      if (sectionRef?.current) {
        sectionRef.current.classList.remove('section-highlight');
      }
    }, 2000);
  }, [sectionHighlight]);

  const handleDifferentialChange = (pathwayNumber: number, value: string) => {
    updateFormField('differentialDiagnosis', `pathway${pathwayNumber}`, value);
  };

  const handlePhysicalExamChange = (section: string, subsection: string, value: string) => {
    updateFormField('physicalExam', `${section}.${subsection}`, value);
  };

  const handleHPIChange = (field: string, value: string) => {
    updateFormField('hpi', field, value);
  };

  const handlePMHChange = (field: string, value: string) => {
    updateFormField('pmh', field, value);
  };

  const handleVitalSignChange = (setIndex: number, field: string, value: any) => {
    const updatedSets = [...vitalSigns.sets];
    
    if (field === 'reading') {
      updatedSets[setIndex] = {
        ...updatedSets[setIndex],
        EKG: {
          ...updatedSets[setIndex].EKG,
          reading: value as string
        }
      };
    } else if (field === 'leadType') {
      // Toggle leadType
      const currentLeadType = updatedSets[setIndex].EKG.leadType;
      const newLeadType = toggleValue(currentLeadType, value as string);
      
      updatedSets[setIndex] = {
        ...updatedSets[setIndex],
        EKG: {
          ...updatedSets[setIndex].EKG,
          leadType: newLeadType
        }
      };
    } else if (field === 'IVGauge') {
      // Toggle IVGauge - if already selected, set to null
      const newValue = updatedSets[setIndex].IVGauge === value ? null : value;
      
      updatedSets[setIndex] = {
        ...updatedSets[setIndex],
        [field]: newValue
      };
    } else if (field === 'pupils' || field === 'lungSounds') {
      // These are multi-select, so no change needed
      updatedSets[setIndex] = {
        ...updatedSets[setIndex],
        [field]: value
      };
    } else {
      // For other fields
      updatedSets[setIndex] = {
        ...updatedSets[setIndex],
        [field]: value
      };
    }
    
    updateFormField('vitalSigns', 'sets', updatedSets);
  };

  const handleReassessmentChange = (value: string) => {
    updateFormField('vitalSigns', 'reassessment', value);
  };

  const renderPhysicalExamField = (section: string, subsection: string, value: string | undefined) => {
    // Check if this is one of the affected field groups
    const isAffectedField = 
      (section === 'rightLeg' || 
       section === 'leftLeg' || 
       section === 'rightArm' || 
       section === 'leftArm' || 
       section === 'posterior');
    
    // If it's an affected field and the value is empty, show the credit message
    if (isAffectedField && (!value || value.trim() === '')) {
      return (
        <div className="flex-grow items center p-2 bg-gray-50 rounded-md">
          <span className="text-red-600 font-bold">
            Provide full credit due to form error (J. Hatcher)
          </span>
        </div>
      );
    }
    
    // Otherwise, render the normal textarea
    return (
      <textarea 
        className="flex-grow items center min-h-[32px] rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
        value={value || ''}
        onChange={(e) => handlePhysicalExamChange(section, subsection, e.target.value)}
      />
    );
  };

  const pupilOptions = [
    { id: 'option-pupils-perrl', label: 'PERRL', value: 'PERRL' },
    { id: 'option-pupils-constricted', label: 'Constricted', value: 'Constricted' },
    { id: 'option-pupils-dilated', label: 'Dilated', value: 'Dilated' },
    { id: 'option-pupils-sluggish', label: 'Sluggish', value: 'Sluggish' },
    { id: 'option-pupils-nonreactive', label: 'Non-reactive', value: 'Non-reactive' }
  ];

  const lungSoundOptions = [
    { id: 'option-lung-clear', label: 'Clear', value: 'Clear' },
    { id: 'option-lung-wheezes', label: 'Wheezes', value: 'Wheezes' },
    { id: 'option-lung-rales', label: 'Rales', value: 'Rales' },
    { id: 'option-lung-rhonchi', label: 'Rhonchi', value: 'Rhonchi' }, // Add missing value
    { id: 'option-lung-diminished', label: 'Diminished', value: 'Diminished' },
    { id: 'option-lung-absent', label: 'Absent', value: 'Absent' }
  ];

  const ivGaugeOptions = [
    { id: 'iv-gauge-14g', label: '14g', value: '14g' },
    { id: 'iv-gauge-16g', label: '16g', value: '16g' },
    { id: 'iv-gauge-18g', label: '18g', value: '18g' },
    { id: 'iv-gauge-20g', label: '20g', value: '20g' },
    { id: 'iv-gauge-22g', label: '22g', value: '22g' },
    { id: 'iv-gauge-24g', label: '24g', value: '24g' }
  ];

  return (
    <div ref={sectionRef} className="bg-gradient-to-br from-teal-50 to-white p-4 rounded-lg mb-6 shadow-md">
      <SectionHeading 
        title="Secondary Assessment" 
        description="Conduct a patient examination and gather a comprehensive medical history that lines up with your field diagnosis."
      />
      
      <div className="space-y-3">
        <div className="space-y-2">
          {/* Differential Diagnosis */}
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <h3 className="text-xl font-semibold text-gray-800 mb-2">Differential Diagnosis</h3>
            <p className="text-base text-gray-600 italic mb-4">
              Directions: Document which 3 pathways you will be choosing as the differential diagnosis for this patient. 
              Pathway 1 should be your highest suspicion, and Pathway 3 should be your least suspected but should still 
              tie in as a possibility. You do not have to elaborate in this section, you are just setting up the foundation 
              for your evaluator to be able to clearly understand your goals for the upcoming sections.
            </p>
            
            <div className="space-y-2">
              <div>
                <label htmlFor="pathway-textarea-0" className="block text-lg font-medium text-gray-700 mb-2">
                  Pathway 1
                </label>
                <textarea 
                  id="pathway-textarea-0" 
                  className="w-full min-h-[40px] rounded-md border-gray-300 shadow-sm text-center focus:border-indigo-500 focus:ring-indigo-500"
                  value={differentialDiagnosis.pathway1}
                  onChange={(e) => handleDifferentialChange(1, e.target.value)}
                />
              </div>
              
              <div>
                <label htmlFor="pathway-textarea-1" className="block text-lg font-medium text-gray-700 mb-2">
                  Pathway 2
                </label>
                <textarea 
                  id="pathway-textarea-1" 
                  className="w-full min-h-[40px] rounded-md border-gray-300 shadow-sm text-center focus:border-indigo-500 focus:ring-indigo-500"
                  value={differentialDiagnosis.pathway2}
                  onChange={(e) => handleDifferentialChange(2, e.target.value)}
                />
              </div>
              
              <div>
                <label htmlFor="pathway-textarea-2" className="block text-lg font-medium text-gray-700 mb-2">
                  Pathway 3
                </label>
                <textarea 
                  id="pathway-textarea-2" 
                  className="w-full min-h-[40px] rounded-md border-gray-300 shadow-sm text-center focus:border-indigo-500 focus:ring-indigo-500"
                  value={differentialDiagnosis.pathway3}
                  onChange={(e) => handleDifferentialChange(3, e.target.value)}
                />
              </div>
            </div>
          </div>
          
{/* Physical Examination */}
<div className="bg-white p-4 rounded-lg shadow-sm">
  <h3 className="text-xl font-semibold text-gray-800 mb-2">Physical Examination</h3>
  <p className="text-sm text-gray-600 italic mb-4">
    Directions: Document the key findings that are relevant to your Diagnosis. Be sure to include all the pertinent negatives specific to each body part. Simply writing "no findings" is not enough for full credit. 
    <span className="font-bold text-red-600">Use this area to document any other tests or examinations you conduct.</span>
  </p>
            
 {/* Head Section */}
<div className="border rounded-lg overflow-hidden">
  <div className="bg-gray-100 px-4 py-2 text-center font-medium">Head:</div>
  <div className="divide-y">
    {/* Eyes */}
    <div className="flex items-center p-2">
      <div className="w-32 flex-shrink-0 text-sm font-medium text-gray-600 text-center">Eyes</div>
      <div className="flex-grow">
        <textarea 
          className="w-full min-h-[60px] rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
          value={physicalExam.head.eyes}
          onChange={(e) => handlePhysicalExamChange('head', 'eyes', e.target.value)}
          rows={2}
        />
      </div>
    </div>
    
    {/* Nose */}
    <div className="flex items-center p-2">
      <div className="w-32 flex-shrink-0 text-sm font-medium text-gray-600 text-center">Nose</div>
      <div className="flex-grow">
        <textarea 
          className="w-full min-h-[60px] rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
          value={physicalExam.head.nose}
          onChange={(e) => handlePhysicalExamChange('head', 'nose', e.target.value)}
          rows={2}
        />
      </div>
    </div>
    
    {/* Ears */}
    <div className="flex items-center p-2">
      <div className="w-32 flex-shrink-0 text-sm font-medium text-gray-600 text-center">Ears</div>
      <div className="flex-grow">
        <textarea 
          className="w-full min-h-[60px] rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
          value={physicalExam.head.ears}
          onChange={(e) => handlePhysicalExamChange('head', 'ears', e.target.value)}
          rows={2}
        />
      </div>
    </div>
    
    {/* Mouth */}
   <div className="flex items-center p-2">
  <div className="w-32 flex-shrink-0 text-sm font-medium text-gray-600 text-center">Mouth</div>
  <div className="flex-grow">
    <textarea 
      className="w-full py-1 px-2 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm resize-none overflow-hidden auto-expand"
      value={physicalExam.head.mouth}
      onChange={(e) => handlePhysicalExamChange('head', 'mouth', e.target.value)}
      rows={1}
    />
  </div>
</div>
  </div>

              
              {/* Neck Section */}
              <div className="border rounded-lg">
                <div className="bg-gray-100 px-4 py-2 text-center font-medium">Neck:</div>
                <div className="divide-y">
                  <div className="flex flex items-center justify-center">
                    <div className="w-32 flex-shrink-0 text-sm font-medium text-gray-600 ">Front</div>
                    <textarea 
                      className="flex-grow items center min-h-[32px] rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                      value={physicalExam.neck.front}
                      onChange={(e) => handlePhysicalExamChange('neck', 'front', e.target.value)}
                    />
                  </div>
                  <div className="flex flex items-center justify-center">
                    <div className="w-32 flex-shrink-0 text-sm font-medium text-gray-600 ">Sides</div>
                    <textarea 
                      className="flex-grow items center min-h-[32px] rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                      value={physicalExam.neck.sides}
                      onChange={(e) => handlePhysicalExamChange('neck', 'sides', e.target.value)}
                    />
                  </div>
                  <div className="flex flex items-center justify-center">
                    <div className="w-32 flex-shrink-0 text-sm font-medium text-gray-600 ">Cervical</div>
                    <textarea 
                      className="flex-grow items center min-h-[32px] rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                      value={physicalExam.neck.cervical}
                      onChange={(e) => handlePhysicalExamChange('neck', 'cervical', e.target.value)}
                    />
                  </div>
                </div>
              </div>
              
              {/* Chest Section */}
              <div className="border rounded-lg">
                <div className="bg-gray-100 px-4 py-2 text-center font-medium">Chest:</div>
                <div className="divide-y">
                  <div className="flex flex items-center justify-center">
                    <div className="w-32 flex-shrink-0 text-sm font-medium text-gray-600 ">Inspection</div>
                    <textarea 
                      className="flex-grow items center min-h-[32px] rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                      value={physicalExam.chest.inspection}
                      onChange={(e) => handlePhysicalExamChange('chest', 'inspection', e.target.value)}
                    />
                  </div>
                  <div className="flex flex items-center justify-center">
                    <div className="w-32 flex-shrink-0 text-sm font-medium text-gray-600 ">Auscultation</div>
                    <textarea 
                      className="flex-grow items center min-h-[32px] rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                      value={physicalExam.chest.auscultation}
                      onChange={(e) => handlePhysicalExamChange('chest', 'auscultation', e.target.value)}
                    />
                  </div>
                  <div className="flex flex items-center justify-center">
                    <div className="w-32 flex-shrink-0 text-sm font-medium text-gray-600 ">Palpation</div>
                    <textarea 
                      className="flex-grow items center min-h-[32px] rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                      value={physicalExam.chest.palpation}
                      onChange={(e) => handlePhysicalExamChange('chest', 'palpation', e.target.value)}
                    />
                  </div>
                </div>
              </div>
              
              {/* Abdomen Section */}
              <div className="border rounded-lg">
                <div className="bg-gray-100 px-4 py-2 text-center font-medium">Abdomen:</div>
                <div className="divide-y">
                  <div className="flex flex items-center justify-center">
                    <div className="w-32 flex-shrink-0 text-sm font-medium text-gray-600 ">Inspection</div>
                    <textarea 
                      className="flex-grow items center min-h-[32px] rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                      value={physicalExam.abdomen.inspection}
                      onChange={(e) => handlePhysicalExamChange('abdomen', 'inspection', e.target.value)}
                    />
                  </div>
                  <div className="flex flex items-center justify-center">
                    <div className="w-32 flex-shrink-0 text-sm font-medium text-gray-600 ">Palpation</div>
                    <textarea 
                      className="flex-grow items center min-h-[32px] rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                      value={physicalExam.abdomen.palpation}
                      onChange={(e) => handlePhysicalExamChange('abdomen', 'palpation', e.target.value)}
                    />
                  </div>
                </div>
              </div>
              
              {/* Pelvis Section */}
              <div className="border rounded-lg">
                <div className="bg-gray-100 px-4 py-2 text-center font-medium">Pelvis:</div>
                <div className="divide-y">
                  <div className="flex flex items-center justify-center">
                    <div className="w-32 flex-shrink-0 text-sm font-medium text-gray-600 ">Palpation</div>
                    <textarea 
                      className="flex-grow items center min-h-[32px] rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                      value={physicalExam.pelvis.palpation}
                      onChange={(e) => handlePhysicalExamChange('pelvis', 'palpation', e.target.value)}
                    />
                  </div>
                  <div className="flex flex items-center justify-center">
                    <div className="w-32 flex-shrink-0 text-sm font-medium text-gray-600 ">Genitalia</div>
                    <textarea 
                      className="flex-grow items center min-h-[32px] rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                      value={physicalExam.pelvis.genitalia}
                      onChange={(e) => handlePhysicalExamChange('pelvis', 'genitalia', e.target.value)}
                    />
                  </div>
                </div>
              </div>
              
              {/* Right Leg Section */}
<div className="border rounded-lg">
  <div className="bg-gray-100 px-4 py-2 text-center font-medium">Right Leg:</div>
  <div className="divide-y">
    <div className="flex flex items-center justify-center">
      <div className="w-32 flex-shrink-0 text-sm font-medium text-gray-600 ">Upper</div>
      {renderPhysicalExamField('rightLeg', 'upper', physicalExam.rightLeg?.upper)}
    </div>
    <div className="flex flex items-center justify-center">
      <div className="w-32 flex-shrink-0 text-sm font-medium text-gray-600 ">Lower</div>
      {renderPhysicalExamField('rightLeg', 'lower', physicalExam.rightLeg?.lower)}
    </div>
  </div>
</div>

{/* Left Leg Section */}
<div className="border rounded-lg">
  <div className="bg-gray-100 px-4 py-2 text-center font-medium">Left Leg:</div>
  <div className="divide-y">
    <div className="flex flex items-center justify-center">
      <div className="w-32 flex-shrink-0 text-sm font-medium text-gray-600 ">Upper</div>
      {renderPhysicalExamField('leftLeg', 'upper', physicalExam.leftLeg?.upper)}
    </div>
    <div className="flex flex items-center justify-center">
      <div className="w-32 flex-shrink-0 text-sm font-medium text-gray-600 ">Lower</div>
      {renderPhysicalExamField('leftLeg', 'lower', physicalExam.leftLeg?.lower)}
    </div>
  </div>
</div>

{/* Right Arm Section */}
<div className="border rounded-lg">
  <div className="bg-gray-100 px-4 py-2 text-center font-medium">Right Arm:</div>
  <div className="divide-y">
    <div className="flex flex items-center justify-center">
      <div className="w-32 flex-shrink-0 text-sm font-medium text-gray-600 ">Upper</div>
      {renderPhysicalExamField('rightArm', 'upper', physicalExam.rightArm?.upper)}
    </div>
    <div className="flex flex items-center justify-center">
      <div className="w-32 flex-shrink-0 text-sm font-medium text-gray-600 ">Lower</div>
      {renderPhysicalExamField('rightArm', 'lower', physicalExam.rightArm?.lower)}
    </div>
  </div>
</div>

{/* Left Arm Section */}
<div className="border rounded-lg">
  <div className="bg-gray-100 px-4 py-2 text-center font-medium">Left Arm:</div>
  <div className="divide-y">
    <div className="flex flex items-center justify-center">
      <div className="w-32 flex-shrink-0 text-sm font-medium text-gray-600 ">Upper</div>
      {renderPhysicalExamField('leftArm', 'upper', physicalExam.leftArm?.upper)}
    </div>
    <div className="flex flex items-center justify-center">
      <div className="w-32 flex-shrink-0 text-sm font-medium text-gray-600 ">Lower</div>
      {renderPhysicalExamField('leftArm', 'lower', physicalExam.leftArm?.lower)}
    </div>
  </div>
</div>

{/* Posterior Section */}
<div className="border rounded-lg">
  <div className="bg-gray-100 px-4 py-2 text-center font-medium">Posterior:</div>
  <div className="divide-y">
    <div className="flex flex items-center justify-center">
      <div className="w-32 flex-shrink-0 text-sm font-medium text-gray-600 ">Thorax</div>
      {renderPhysicalExamField('posterior', 'thorax', physicalExam.posterior?.thorax)}
    </div>
    <div className="flex flex items-center justify-center">
      <div className="w-32 flex-shrink-0 text-sm font-medium text-gray-600 ">Lumbar</div>
      {renderPhysicalExamField('posterior', 'lumbar', physicalExam.posterior?.lumbar)}
    </div>
    <div className="flex flex items-center justify-center">
      <div className="w-32 flex-shrink-0 text-sm font-medium text-gray-600 ">Buttocks</div>
      {renderPhysicalExamField('posterior', 'buttocks', physicalExam.posterior?.buttocks)}
    </div>
  </div>
</div>
            </div>
          </div>
          
          {/* History of Present Illness */}
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <h3 id="hpi-header" className="block text-sm font-medium text-gray-700 mb-2">History of Present Illness</h3>
            <div className="bg-gray-50 p-4 rounded" aria-labelledby="hpi-header">
              <div className="mb-2">
                <label htmlFor="hpi-onset" className="block text-xs font-medium text-gray-600 mb-2">Onset</label>
                <textarea 
                  id="hpi-onset" 
                  className="w-full text-sm rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  value={hpi.onset}
                  onChange={(e) => handleHPIChange('onset', e.target.value)}
                />
              </div>
              <div className="mb-2">
                <label htmlFor="hpi-provocation" className="block text-xs font-medium text-gray-600 mb-2">Provocation</label>
                <textarea 
                  id="hpi-provocation" 
                  className="w-full text-sm rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  value={hpi.provocation}
                  onChange={(e) => handleHPIChange('provocation', e.target.value)}
                />
              </div>
              <div className="mb-2">
                <label htmlFor="hpi-quality" className="block text-xs font-medium text-gray-600 mb-2">Quality</label>
                <textarea 
                  id="hpi-quality" 
                  className="w-full text-sm rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  value={hpi.quality}
                  onChange={(e) => handleHPIChange('quality', e.target.value)}
                />
              </div>
              <div className="mb-2">
                <label htmlFor="hpi-radiate/region" className="block text-xs font-medium text-gray-600 mb-2">Radiate/Region</label>
                <textarea 
                  id="hpi-radiate/region" 
                  className="w-full text-sm rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  value={hpi.radiateRegion}
                  onChange={(e) => handleHPIChange('radiateRegion', e.target.value)}
                />
              </div>
              <div className="mb-2">
                <label htmlFor="hpi-severity" className="block text-xs font-medium text-gray-600 mb-2">Severity</label>
                <textarea 
                  id="hpi-severity" 
                  className="w-full text-sm rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  value={hpi.severity}
                  onChange={(e) => handleHPIChange('severity', e.target.value)}
                />
              </div>
              <div className="mb-2">
                <label htmlFor="hpi-time" className="block text-xs font-medium text-gray-600 mb-2">Time</label>
                <textarea 
                  id="hpi-time" 
                  className="w-full text-sm rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  value={hpi.time}
                  onChange={(e) => handleHPIChange('time', e.target.value)}
                />
              </div>
              <div className="mb-2">
                <label htmlFor="hpi-clarifying-questions-related-to-signs/symptoms" className="block text-xs font-medium text-gray-600 mb-2">Clarifying questions related to signs/symptoms</label>
                <textarea 
                  id="hpi-clarifying-questions-related-to-signs/symptoms" 
                  className="w-full text-sm rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  value={hpi.clarifyingQuestions}
                  onChange={(e) => handleHPIChange('clarifyingQuestions', e.target.value)}
                />
              </div>
            </div>
          </div>
          
          {/* Past Medical History */}
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <h3 id="pmh-header" className="block text-sm font-medium text-gray-700 mb-2">Past Medical History (AMPLE)</h3>
            <div className="bg-gray-50 p-4 rounded" aria-labelledby="pmh-header">
              <div className="mb-2">
                <label htmlFor="pmh-allergies" className="block text-xs font-medium text-gray-600 mb-2">Allergies</label>
                <textarea 
                  id="pmh-allergies" 
                  className="w-full text-sm rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  value={pmh.allergies}
                  onChange={(e) => handlePMHChange('allergies', e.target.value)}
                />
              </div>
              <div className="mb-2">
                <label htmlFor="pmh-medications" className="block text-xs font-medium text-gray-600 mb-2">Medications</label>
                <textarea 
                  id="pmh-medications" 
                  className="w-full text-sm rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  value={pmh.medications}
                  onChange={(e) => handlePMHChange('medications', e.target.value)}
                />
              </div>
              <div className="mb-2">
                <label htmlFor="pmh-pertinent-past-history" className="block text-xs font-medium text-gray-600 mb-2">Pertinent Past History</label>
                <textarea 
                  id="pmh-pertinent-past-history" 
                  className="w-full text-sm rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  value={pmh.pertinentPastHistory}
                  onChange={(e) => handlePMHChange('pertinentPastHistory', e.target.value)}
                />
              </div>
              <div className="mb-2">
                <label htmlFor="pmh-last-oral-intake" className="block text-xs font-medium text-gray-600 mb-2">Last Oral Intake</label>
                <textarea 
                  id="pmh-last-oral-intake" 
                  className="w-full text-sm rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  value={pmh.lastOralIntake}
                  onChange={(e) => handlePMHChange('lastOralIntake', e.target.value)}
                />
              </div>
              <div className="mb-2">
                <label htmlFor="pmh-events-leading-to-illness/injury" className="block text-xs font-medium text-gray-600 mb-2">Events Leading to Illness/Injury</label>
                <textarea 
                  id="pmh-events-leading-to-illness/injury" 
                  className="w-full text-sm rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  value={pmh.eventsLeadingToIllness}
                  onChange={(e) => handlePMHChange('eventsLeadingToIllness', e.target.value)}
                />
              </div>
            </div>
          </div>
          
          {/* Vital Signs */}
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <h3 className="text-xl font-semibold text-gray-800 mb-2">Vital Signs</h3>
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Initial Vitals */}
                <div className="border rounded-lg">
                  <div className="bg-gray-100 px-4 py-2 flex justify-between items-center">
                    <span className="font-medium">Initial Vitals</span>
                    <input 
                      id="time-input-0" 
                      type="text" 
                      placeholder="Time" 
                      className="text-center w-1/3 shadow-sm shadow-gray-300 rounded-md border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
                      value={vitalSigns.sets[0].time}
                      onChange={(e) => handleVitalSignChange(0, 'time', e.target.value)}
                      aria-label="Time for Initial Vitals"
                    />
                  </div>
                  <div className="text-center p-4 space-y-2">
                    <div className="space-y-2">
                      <label htmlFor="vital-input-0-0" className="block text-sm font-black text-gray-700">Blood Pressure</label>
                      <input 
                        id="vital-input-0-0" 
                        type="text" 
                        className="text-center w-3/4 py-1 shadow-sm shadow-gray-300 rounded-md border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
                        value={vitalSigns.sets[0].bloodPressure}
                        onChange={(e) => handleVitalSignChange(0, 'bloodPressure', e.target.value)}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <label htmlFor="vital-input-0-1" className="block text-sm font-black text-gray-700">Heart Rate</label>
                      <input 
                        id="vital-input-0-1" 
                        type="text" 
                        className="text-center w-3/4 py-1 shadow-sm shadow-gray-300 rounded-md border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
                        value={vitalSigns.sets[0].heartRate}
                        onChange={(e) => handleVitalSignChange(0, 'heartRate', e.target.value)}
                      />
                    </div>
                    
                    <div className="text-center space-y-2">
                      <label htmlFor="text-center font-black vital-input-0-2" className="font-black block text-center text-sm text-gray-700">Respiratory Rate</label>
                      <input 
                        id="vital-input-0-2" 
                        type="text" 
                        className="text-center w-3/4 py-1 shadow-sm shadow-gray-300 rounded-md border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
                        value={vitalSigns.sets[0].respiratoryRate}
                        onChange={(e) => handleVitalSignChange(0, 'respiratoryRate', e.target.value)}
                      />
                    </div>
                    
                    <div className="text-center space-y-2">
                      <label htmlFor="vital-input-0-3" className="block text-sm font-black text-gray-700">SpO2</label>
                      <input 
                        id="vital-input-0-3" 
                        type="text" 
                        className="text-center w-3/4 py-1 shadow-sm shadow-gray-300 rounded-md border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
                        value={vitalSigns.sets[0].spO2}
                        onChange={(e) => handleVitalSignChange(0, 'spO2', e.target.value)}
                      />
                    </div>
                    
                    <div className="text-center space-y-2">
                      <label htmlFor="vital-input-0-4" className="block text-sm font-black text-gray-700">BGL</label>
                      <input 
                        id="vital-input-0-4" 
                        type="text" 
                        className="text-center w-3/4 py-1 shadow-sm shadow-gray-300 rounded-md border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
                        value={vitalSigns.sets[0].BGL}
                        onChange={(e) => handleVitalSignChange(0, 'BGL', e.target.value)}
                      />
                    </div>
                    
                    <div className="bg-gray-50 p-3 rounded-lg shadow-md space-y-2">
                      <h4 className="bg-gray-50 py-1 rounded-lg text-sm text-center font-black text-gray-700">IV Gauge</h4>
                      <div className="grid grid-cols-3 gap-x-4 gap-y-2">
                        {ivGaugeOptions.map((option, index) => (
                          <label 
                            key={`iv-gauge-0-${index}`}
                            className="shadow-sm shadow-gray-300 bg-white rounded-lg text-center space-x-2" 
                            htmlFor={`iv-gauge-0-${index}`}
                          >
                            <input 
                              id={`iv-gauge-0-${index}`} 
                              type="checkbox" 
                              className="text-center rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                              checked={vitalSigns.sets[0].IVGauge === option.value}
                              onChange={() => handleVitalSignChange(0, 'IVGauge', option.value)}
                            />
                            <span className="text-sm text-gray-700">{option.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    
                    <div className="bg-gray-50 p-3 rounded-lg shadow-md space-y-2">
                      <h4 className="text-sm text-center font-black text-gray-700">EKG</h4>
                      <input 
                        id="ekg-input-0" 
                        type="text" 
                        className="text-center w-full rounded-md border-gray-300 shadow-sm shadow-gray-300 focus:border-indigo-500 focus:ring-indigo-500 mb-2"
                        value={vitalSigns.sets[0].EKG.reading}
                        onChange={(e) => handleVitalSignChange(0, 'reading', e.target.value)}
                        aria-label="EKG reading for Initial Vitals"
                      />
                      <div className="text-center grid grid-cols-2 gap-2">
                        <label className="shadow-sm shadow-gray-300 rounded-md bg-white flex items-center justify-center space-x-2" htmlFor="limb-leads-0">
                          <input 
                            id="limb-leads-0" 
                            type="checkbox" 
                            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                            checked={vitalSigns.sets[0].EKG.leadType === 'Limb Leads'}
                            onChange={() => handleVitalSignChange(0, 'leadType', 'Limb Leads')}
                          />
                          <span className="justify-center text-sm text-gray-700">Limb Leads</span>
                        </label>
                        <label className="shadow-sm shadow-gray-300 rounded-md  bg-white flex items-center justify-center space-x-2" htmlFor="twelve-lead-0">
                          <input 
                            id="twelve-lead-0" 
                            type="checkbox" 
                            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                            checked={vitalSigns.sets[0].EKG.leadType === '12 Lead'}
                            onChange={() => handleVitalSignChange(0, 'leadType', '12 Lead')}
                          />
                          <span className="text-center text-sm text-gray-700">12 Lead</span>
                        </label>
                      </div>
                    </div>
                    
                    <div className="bg-gray-50 p-3 rounded-lg shadow-md space-y-2">
  <h4 className="text-sm text-center font-black text-gray-700">Pupils</h4>
  <div className="grid grid-cols-2 gap-x-4 gap-y-2">
    {pupilOptions.map((option, index) => (
      <label 
        key={`option-0-0-${index}`}
        className="flex items-center space-x-1 p-1.5 px-2 bg-white shadow-sm shadow-gray-300 rounded-md hover:bg-gray-50" 
        htmlFor={`option-0-0-${index}`}
      >
        <input 
          id={`option-0-0-${index}`} 
          type="checkbox" 
          className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
          checked={vitalSigns.sets[0].pupils.includes(option.value) || false}
          onChange={(e) => {
            const updatedPupils = e.target.checked 
              ? [...vitalSigns.sets[0].pupils, option.value || '']
              : vitalSigns.sets[0].pupils.filter(v => v !== option.value || '');
            handleVitalSignChange(0, 'pupils', updatedPupils);
          }}
        />
        <span className="text-xs sm:text-sm text-gray-700">{option.label}</span>
      </label>
    ))}
  </div>
</div>
                    
                    <div className="bg-gray-50 p-3 rounded-lg shadow-md space-y-2">
                      <h4 className="text-sm text-center font-black text-gray-700">Lung Sounds</h4>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                        {lungSoundOptions.map((option, index) => (
                          <label 
                            key={`option-0-1-${index}`}
                            className="shadow-sm shadow-gray-300 rounded-md  bg-white flex items-center space-x-2" 
                            htmlFor={`option-0-1-${index}`}
                          >
                            <input 
                              id={`option-0-1-${index}`} 
                              type="checkbox" 
                              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                              checked={vitalSigns.sets[0].lungSounds?.includes(option.value) || false}
                              onChange={(e) => {
                                const updatedLungSounds = e.target.checked 
                                  ? [...vitalSigns.sets[0].lungSounds, option?.value || '']
                                  : vitalSigns.sets[0].lungSounds.filter(v => v !== option?.value || '');
                                handleVitalSignChange(0, 'lungSounds', updatedLungSounds);
                              }}
                            />
                            <span className="text-sm text-gray-700">{option.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Second Set of Vitals */}
<div className="border rounded-lg">
  <div className="bg-gray-100 px-4 py-2 flex justify-between items-center">
    <span className="font-medium">2nd Set</span>
    <input 
      id="time-input-1" 
      type="text" 
      placeholder="Time" 
      className="text-center w-1/3 shadow-sm shadow-gray-300 rounded-md border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
      value={vitalSigns.sets[1].time}
      onChange={(e) => handleVitalSignChange(1, 'time', e.target.value)}
      aria-label="Time for 2nd Set"
    />
  </div>
  <div className="text-center p-4 space-y-2">
    <div className="space-y-2">
      <label htmlFor="vital-input-1-0" className="block text-sm font-black text-gray-700">Blood Pressure</label>
      <input 
        id="vital-input-1-0" 
        type="text" 
        className="text-center w-3/4 py-1 shadow-sm shadow-gray-300 rounded-md border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
        value={vitalSigns.sets[1].bloodPressure}
        onChange={(e) => handleVitalSignChange(1, 'bloodPressure', e.target.value)}
      />
    </div>
    
    <div className="space-y-2">
      <label htmlFor="vital-input-1-1" className="block text-sm font-black text-gray-700">Heart Rate</label>
      <input 
        id="vital-input-1-1" 
        type="text" 
        className="text-center w-3/4 py-1 shadow-sm shadow-gray-300 rounded-md border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
        value={vitalSigns.sets[1].heartRate}
        onChange={(e) => handleVitalSignChange(1, 'heartRate', e.target.value)}
      />
    </div>
    
    <div className="text-center space-y-2">
      <label htmlFor="vital-input-1-2" className="block text-center text-sm font-black text-gray-700">Respiratory Rate</label>
      <input 
        id="vital-input-1-2" 
        type="text" 
        className="text-center w-3/4 py-1 shadow-sm shadow-gray-300 rounded-md border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
        value={vitalSigns.sets[1].respiratoryRate}
        onChange={(e) => handleVitalSignChange(1, 'respiratoryRate', e.target.value)}
      />
    </div>
    
    <div className="text-center space-y-2">
      <label htmlFor="vital-input-1-3" className="block text-sm font-black text-gray-700">SpO2</label>
      <input 
        id="vital-input-1-3" 
        type="text" 
        className="text-center w-3/4 py-1 shadow-sm shadow-gray-300 rounded-md border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
        value={vitalSigns.sets[1].spO2}
        onChange={(e) => handleVitalSignChange(1, 'spO2', e.target.value)}
      />
    </div>
    
    <div className="text-center space-y-2">
      <label htmlFor="vital-input-1-4" className="block text-sm font-black text-gray-700">BGL</label>
      <input 
        id="vital-input-1-4" 
        type="text" 
        className="text-center w-3/4 py-1 shadow-sm shadow-gray-300 rounded-md border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
        value={vitalSigns.sets[1].BGL}
        onChange={(e) => handleVitalSignChange(1, 'BGL', e.target.value)}
      />
    </div>
    
    <div className="bg-gray-50 p-3 rounded-lg shadow-md space-y-2">
      <h4 className="bg-gray-50 py-1 rounded-lg text-sm text-center font-black text-gray-700">IV Gauge</h4>
      <div className="grid grid-cols-3 gap-x-4 gap-y-2">
        {ivGaugeOptions.map((option, index) => (
          <label 
            key={`iv-gauge-1-${index}`}
            className="shadow-sm shadow-gray-300 bg-white rounded-lg text-center space-x-2" 
            htmlFor={`iv-gauge-1-${index}`}
          >
            <input 
              id={`iv-gauge-1-${index}`} 
              type="checkbox" 
              className="text-center rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              checked={vitalSigns.sets[1].IVGauge === option.value}
              onChange={() => handleVitalSignChange(1, 'IVGauge', option.value)}
            />
            <span className="text-sm text-gray-700">{option.label}</span>
          </label>
        ))}
      </div>
    </div>
    
    <div className="bg-gray-50 p-3 rounded-lg shadow-md space-y-2">
      <h4 className="text-sm text-center font-black text-gray-700">EKG</h4>
      <input 
        id="ekg-input-1" 
        type="text" 
        className="text-center w-full rounded-md border-gray-300 shadow-sm shadow-gray-300 focus:border-indigo-500 focus:ring-indigo-500 mb-2"
        value={vitalSigns.sets[1].EKG.reading}
        onChange={(e) => handleVitalSignChange(1, 'reading', e.target.value)}
        aria-label="EKG reading for 2nd Set"
      />
      <div className="text-center grid grid-cols-2 gap-2">
        <label className="shadow-sm shadow-gray-300 rounded-md bg-white flex items-center justify-center space-x-2" htmlFor="limb-leads-1">
          <input 
            id="limb-leads-1" 
            type="checkbox" 
            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            checked={vitalSigns.sets[1].EKG.leadType === 'Limb Leads'}
            onChange={() => handleVitalSignChange(1, 'leadType', 'Limb Leads')}
          />
          <span className="justify-center text-sm text-gray-700">Limb Leads</span>
        </label>
        <label className="shadow-sm shadow-gray-300 rounded-md bg-white flex items-center justify-center space-x-2" htmlFor="twelve-lead-1">
          <input 
            id="twelve-lead-1" 
            type="checkbox" 
            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            checked={vitalSigns.sets[1].EKG.leadType === '12 Lead'}
            onChange={() => handleVitalSignChange(1, 'leadType', '12 Lead')}
          />
          <span className="text-center text-sm text-gray-700">12 Lead</span>
        </label>
      </div>
    </div>
                    
    <div className="bg-gray-50 p-3 rounded-lg shadow-md space-y-2">
  <h4 className="text-sm text-center font-black text-gray-700">Pupils</h4>
  <div className="grid grid-cols-2 gap-x-4 gap-y-2">
    {pupilOptions.map((option, index) => (
      <label 
        key={`option-1-0-${index}`}
        className="flex items-center space-x-1 p-1.5 px-2 bg-white shadow-sm shadow-gray-300 rounded-md hover:bg-gray-50" 
        htmlFor={`option-1-0-${index}`}
      >
        <input 
          id={`option-1-0-${index}`} 
          type="checkbox" 
          className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
          checked={vitalSigns.sets[1].pupils.includes(option.value) || false}
          onChange={(e) => {
            const updatedPupils = e.target.checked 
              ? [...vitalSigns.sets[1].pupils, option?.value || '']
              : vitalSigns.sets[1].pupils.filter(v => v !== option?.value || '');
            handleVitalSignChange(1, 'pupils', updatedPupils);
          }}
        />
        <span className="text-xs sm:text-sm text-gray-700">{option.label}</span>
      </label>
    ))}
  </div>
</div>

<div className="bg-gray-50 p-3 rounded-lg shadow-md space-y-2">
  <h4 className="text-sm text-center font-black text-gray-700">Lung Sounds</h4>
  <div className="grid grid-cols-2 gap-x-4 gap-y-2">
    {lungSoundOptions.map((option, index) => (
      <label 
        key={`option-1-1-${index}`}
        className="shadow-sm shadow-gray-300 rounded-md bg-white flex items-center space-x-2" 
        htmlFor={`option-1-1-${index}`}
      >
        <input 
          id={`option-1-1-${index}`} 
          type="checkbox" 
          className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
          checked={vitalSigns.sets[1].lungSounds?.includes(option.value) || false}
          onChange={(e) => {
            const updatedLungSounds = e.target.checked 
              ? [...vitalSigns.sets[1].lungSounds, option?.value || '']
              : vitalSigns.sets[1].lungSounds.filter(v => v !== option?.value || '');
            handleVitalSignChange(1, 'lungSounds', updatedLungSounds);
          }}
        />
        <span className="text-xs sm:text-sm text-gray-700">{option.label}</span>
      </label>
    ))}
  </div>
</div>
                  </div>
                </div>
                                    
                                  {/* Third Set of Vitals */}
<div className="border rounded-lg">
  <div className="bg-gray-100 px-4 py-2 flex justify-between items-center">
    <span className="font-medium">3rd Set</span>
    <input 
      id="time-input-2" 
      type="text" 
      placeholder="Time" 
      className="text-center w-1/3 shadow-sm shadow-gray-300 rounded-md border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
      value={vitalSigns.sets[2].time}
      onChange={(e) => handleVitalSignChange(2, 'time', e.target.value)}
      aria-label="Time for 3rd Set"
    />
  </div>
  <div className="text-center p-4 space-y-2">
    <div className="space-y-2">
      <label htmlFor="vital-input-2-0" className="block text-sm font-black text-gray-700">Blood Pressure</label>
      <input 
        id="vital-input-2-0" 
        type="text" 
        className="text-center w-3/4 py-1 shadow-sm shadow-gray-300 rounded-md border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
        value={vitalSigns.sets[2].bloodPressure}
        onChange={(e) => handleVitalSignChange(2, 'bloodPressure', e.target.value)}
      />
    </div>
    
    <div className="space-y-2">
      <label htmlFor="vital-input-2-1" className="block text-sm font-black text-gray-700">Heart Rate</label>
      <input 
        id="vital-input-2-1" 
        type="text" 
        className="text-center w-3/4 py-1 shadow-sm shadow-gray-300 rounded-md border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
        value={vitalSigns.sets[2].heartRate}
        onChange={(e) => handleVitalSignChange(2, 'heartRate', e.target.value)}
      />
    </div>
    
    <div className="text-center space-y-2">
      <label htmlFor="vital-input-2-2" className="block text-center text-sm font-black text-gray-700">Respiratory Rate</label>
      <input 
        id="vital-input-2-2" 
        type="text" 
        className="text-center w-3/4 py-1 shadow-sm shadow-gray-300 rounded-md border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
        value={vitalSigns.sets[2].respiratoryRate}
        onChange={(e) => handleVitalSignChange(2, 'respiratoryRate', e.target.value)}
      />
    </div>
    
    <div className="text-center space-y-2">
      <label htmlFor="vital-input-2-3" className="block text-sm font-black text-gray-700">SpO2</label>
      <input 
        id="vital-input-2-3" 
        type="text" 
        className="text-center w-3/4 py-1 shadow-sm shadow-gray-300 rounded-md border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
        value={vitalSigns.sets[2].spO2}
        onChange={(e) => handleVitalSignChange(2, 'spO2', e.target.value)}
      />
    </div>
    
    <div className="text-center space-y-2">
      <label htmlFor="vital-input-2-4" className="block text-sm font-black text-gray-700">BGL</label>
      <input 
        id="vital-input-2-4" 
        type="text" 
        className="text-center w-3/4 py-1 shadow-sm shadow-gray-300 rounded-md border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
        value={vitalSigns.sets[2].BGL}
        onChange={(e) => handleVitalSignChange(2, 'BGL', e.target.value)}
      />
    </div>
    
    <div className="bg-gray-50 p-3 rounded-lg shadow-md space-y-2">
      <h4 className="bg-gray-50 py-1 rounded-lg text-sm text-center font-black text-gray-700">IV Gauge</h4>
      <div className="grid grid-cols-3 gap-x-4 gap-y-2">
        {ivGaugeOptions.map((option, index) => (
          <label 
            key={`iv-gauge-2-${index}`}
            className="shadow-sm shadow-gray-300 bg-white rounded-lg text-center space-x-2" 
            htmlFor={`iv-gauge-2-${index}`}
          >
            <input 
              id={`iv-gauge-2-${index}`} 
              type="checkbox" 
              className="text-center rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              checked={vitalSigns.sets[2].IVGauge === option.value}
              onChange={() => handleVitalSignChange(2, 'IVGauge', option.value)}
            />
            <span className="text-sm text-gray-700">{option.label}</span>
          </label>
        ))}
      </div>
    </div>
    
    <div className="bg-gray-50 p-3 rounded-lg shadow-md space-y-2">
      <h4 className="text-sm text-center font-black text-gray-700">EKG</h4>
      <input 
        id="ekg-input-2" 
        type="text" 
        className="text-center w-full rounded-md border-gray-300 shadow-sm shadow-gray-300 focus:border-indigo-500 focus:ring-indigo-500 mb-2"
        value={vitalSigns.sets[2].EKG.reading}
        onChange={(e) => handleVitalSignChange(2, 'reading', e.target.value)}
        aria-label="EKG reading for 3rd Set"
      />
      <div className="text-center grid grid-cols-2 gap-2">
        <label className="shadow-sm shadow-gray-300 rounded-md bg-white flex items-center justify-center space-x-2" htmlFor="limb-leads-2">
          <input 
            id="limb-leads-2" 
            type="checkbox" 
            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            checked={vitalSigns.sets[2].EKG.leadType === 'Limb Leads'}
            onChange={() => handleVitalSignChange(2, 'leadType', 'Limb Leads')}
          />
          <span className="justify-center text-sm text-gray-700">Limb Leads</span>
        </label>
        <label className="shadow-sm shadow-gray-300 rounded-md bg-white flex items-center justify-center space-x-2" htmlFor="twelve-lead-2">
          <input 
            id="twelve-lead-2" 
            type="checkbox" 
            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            checked={vitalSigns.sets[2].EKG.leadType === '12 Lead'}
            onChange={() => handleVitalSignChange(2, 'leadType', '12 Lead')}
          />
          <span className="text-center text-sm text-gray-700">12 Lead</span>
        </label>
      </div>
    </div>
    
    <div className="bg-gray-50 p-3 rounded-lg shadow-md space-y-2">
      <h4 className="text-sm text-center font-black text-gray-700">Pupils</h4>
      <div className="grid grid-cols-2 gap-x-4 gap-y-2">
        {pupilOptions.map((option, index) => (
          <label 
            key={`option-2-0-${index}`}
            className="flex items-center space-x-1 p-1.5 px-2 bg-white shadow-sm shadow-gray-300 rounded-md hover:bg-gray-50" 
            htmlFor={`option-2-0-${index}`}
          >
            <input 
              id={`option-2-0-${index}`} 
              type="checkbox" 
              className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              checked={vitalSigns.sets[2].pupils.includes(option.value) || false}
              onChange={(e) => {
                const updatedPupils = e.target.checked 
                  ? [...vitalSigns.sets[2].pupils, option?.value || '']
                  : vitalSigns.sets[2].pupils.filter(v => v !== option?.value || '');
                handleVitalSignChange(2, 'pupils', updatedPupils);
              }}
            />
            <span className="text-xs sm:text-sm text-gray-700">{option.label}</span>
          </label>
        ))}
      </div>
    </div>
    
    <div className="bg-gray-50 p-3 rounded-lg shadow-md space-y-2">
      <h4 className="text-sm text-center font-black text-gray-700">Lung Sounds</h4>
      <div className="grid grid-cols-2 gap-x-4 gap-y-2">
        {lungSoundOptions.map((option, index) => (
          <label 
            key={`option-2-1-${index}`}
            className="shadow-sm shadow-gray-300 rounded-md bg-white flex items-center space-x-2" 
            htmlFor={`option-2-1-${index}`}
          >
            <input 
              id={`option-2-1-${index}`} 
              type="checkbox" 
              className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              checked={vitalSigns.sets[2].lungSounds?.includes(option.value) || false}
              onChange={(e) => {
                const updatedLungSounds = e.target.checked 
                  ? [...vitalSigns.sets[2].lungSounds, option?.value || '']
                  : vitalSigns.sets[2].lungSounds.filter(v => v !== option?.value || '');
                handleVitalSignChange(2, 'lungSounds', updatedLungSounds);
              }}
            />
            <span className="text-xs sm:text-sm text-gray-700">{option.label}</span>
          </label>
        ))}
      </div>
    </div>

            
                    {/* Continue with similar input fields for other vital signs */}
                  </div>
                </div>
              </div>
              
              <div className="mt-6">
                <label htmlFor="reassessment-textarea" className="block text-lg font-medium text-gray-700 mb-2">Reassessment</label>
                <textarea 
                  id="reassessment-textarea" 
                  className="w-full min-h-[100px] rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  value={vitalSigns.reassessment}
                  onChange={(e) => handleReassessmentChange(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecondaryAssessment;